import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import EstimateBar from '../components/EstimateBar';
import CurrencyFormat from '../components/CurrencyFormat';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DateFormat from '../components/DateFormat';
import { TrashIcon } from '@heroicons/react/24/outline';
import { ca } from 'date-fns/locale';

const EstimateProposal = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { id, key } = useParams();
  const navigate = useNavigate();

  const [proposalData, setProposalData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showDwgDateModal, setShowDwgDateModal] = useState(false);
  const [dwgDate, setDwgDate] = useState('');
  const [downloading, setDownloading] = useState(false);

  // Local YYYY-MM-DD (no UTC)
  const toLocalISO = (d) => {
    const date = new Date(d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Fetch wrapped in useCallback so other handlers can refetch without reload
  const fetchProposal = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/best_crud.php`, {
        params: {
          estimate: id,
          builder_id: key,
          estimateProposal: true,
        },
        withCredentials: true,
      });
      if (response.data?.[0] === 'success') {
        // The payload uses array-with-keyed object: ['success', ...keys]
        // Keep the whole object; it has both numeric '0' and associative keys.
        setProposalData(response.data);
      } else {
        toast.error('Proposal not found', { position: 'top-center' });
      }
    } catch (error) {
      // Redirect only if unauthorized
      if (error?.response?.status === 401) navigate('/login');
      else toast.error('Failed to load proposal', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  }, [apiUrl, id, key, navigate]);

  useEffect(() => {
    if (id && key) fetchProposal();
  }, [id, key, fetchProposal]);

  // When the API loads, check for dwg_date_confirmation
  useEffect(() => {
    if (!proposalData) return;

    const c = proposalData?.dwg_date_confirmation;
    if (c === false) {
      setShowDwgDateModal(true);
    } else {
      // normalize: store array/object/string as-is; we’ll derive value below
      setDwgDate(c || '');
    }
  }, [proposalData]);

  if (loading) return <div className="p-10 text-center">Loading Proposal...</div>;
  if (!proposalData) return <div className="p-10 text-center text-red-600">Proposal not found.</div>;

  // Safe destructure with fallbacks
  const {
    estimate = {},
    builder = {},
    notes = [],
    equipment_list = [],
    custom_notes = {},
    system_type = '',
  } = proposalData ?? {};

  // Extract final price whether scalar or { final_price: N }
  const finalPrice =
    (proposalData?.final_price && typeof proposalData.final_price === 'object'
      ? proposalData.final_price.final_price
      : proposalData?.final_price) ?? null;

const alternates = Array.isArray(proposalData?.alternative_price)
  ? proposalData.alternative_price
  : proposalData?.alternative_price && typeof proposalData.alternative_price === 'object'
    ? [proposalData.alternative_price]
    : [];
  const customNotesForCat = (cat) => {
    if (!custom_notes) return [];
    const k = Object.keys(custom_notes).find((keyName) =>
      keyName?.toUpperCase?.().includes(cat.toUpperCase())
    );
    if (!k) return [];
    const val = custom_notes[k];
    return Array.isArray(val) ? val : [val];
  };

  // Find all qualifications/terms from both standard and custom
  const standardQualifications = (notes || [])
    .filter((n) => n?.cat?.toUpperCase?.().includes('QUALIFICATIONS'))
    .flatMap((n) => n.list || []);
  const customQualifications = customNotesForCat('QUALIFICATIONS');
  const allQualifications = [...standardQualifications, ...customQualifications];

  const standardTerms = (notes || [])
    .filter((n) => n?.cat?.toUpperCase?.().includes('TERMS'))
    .flatMap((n) => n.list || []);
  const customTerms = customNotesForCat('TERMS');
  const allTerms = [...standardTerms, ...customTerms];

  const projectDate = estimate.date || builder.date || toLocalISO(new Date());
  const contact = estimate || {};
  const jobLocation = estimate.bidding_address || '';
  const userName = builder.user_name || 'Fire Gear., Inc.';

  // DWG date normalization (record + value)
  const dwgRecord = Array.isArray(dwgDate) ? dwgDate[0] : dwgDate;
  const dwgDateId = dwgRecord?.id;
  const dwgDateVal =
    (Array.isArray(dwgDate) ? dwgDate[0]?.dwg_date : (dwgDate?.dwg_date ?? dwgDate)) || '';

  // Address block
  const renderAddressBlock = () => (
    <>
      <div className="font-bold">{contact.att}</div>
      <div>{contact.contact_name !== 0 && <span>{contact.contact_name}</span>}</div>
      <div>{contact.contact_address !== 0 && <span>{contact.contact_address}</span>}</div>
      <div>
        {contact.contact_city !== 0 && <span>{contact.contact_city}</span>}{' '}
       {contact.contact_state !== 0 && <span>{contact.contact_state}</span>} {contact.contact_zip !== 0 && <span>{contact.contact_zip}</span>}
      </div>
    </>
  );

  // Basic number-to-words (1..)
  const numberToWords = (n, capitalize = false) => {
    n = Number(n) || 0;
    
    const words = [
      '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
    ];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const scales = ['', 'Thousand', 'Million', 'Billion'];
    if (n === 0) return 'Zero';

    const chunkToWords = (num) => {
      let str = '';
      if (num >= 100) {
        str += words[Math.floor(num / 100)] + ' Hundred';
        num %= 100;
        if (num > 0) str += ' ';
      }
      if (num >= 20) {
        str += tens[Math.floor(num / 10)];
        num %= 10;
        if (num > 0) str += '-' + words[num];
      } else if (num > 0) {
        str += words[num];
      }
      return str;
    };

    const chunks = [];
    while (n > 0) {
      chunks.push(n % 1000);
      n = Math.floor(n / 1000);
    }
    return chunks
      .map((chunk, i) => (chunk ? chunkToWords(chunk) + (scales[i] ? ' ' + scales[i] : '') : ''))
      .filter(Boolean)
      .reverse()
      .join(' ');
  };

  function numberToWordsUSD(n) {
    if (n == null || isNaN(n)) return '';
    const ones = [
      '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
      'Seventeen', 'Eighteen', 'Nineteen',
    ];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    function words(num) {
      if (num < 20) return ones[num];
      if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
      if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + words(num % 100) : '');
      if (num < 1000000) return words(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + words(num % 1000) : '');
      if (num < 1000000000) return words(Math.floor(num / 1000000)) + ' Million' + (num % 1000000 ? ' ' + words(num % 1000000) : '');
      return '';
    }

    const [dollars, cents] = parseFloat(n).toFixed(2).split('.');
    let result = words(parseInt(dollars)) + ' Dollar' + (parseInt(dollars) === 1 ? '' : 's');
    if (parseInt(cents) > 0) {
      result += ' and ' + words(parseInt(cents)) + ' Cent' + (parseInt(cents) === 1 ? '' : 's');
    }
    return result.trim();
  }

  // Sorted equipment
  const equipmentSorted = [
    // Panel or command station first
    ...equipment_list.filter((eq) => /panel|FIRE COMMAND STATION/i.test(String(eq?.cat ?? ''))),
    // DGP or data gathering panel second
    ...equipment_list.filter(
      (eq) => !/panel|FIRE COMMAND STATION/i.test(String(eq?.cat ?? '')) && /dgp|data gathering panel/i.test(String(eq?.cat ?? ''))
    ),
    // Rest
    ...equipment_list.filter(
      (eq) =>
        !/panel|FIRE COMMAND STATION/i.test(String(eq?.cat ?? '')) &&
        !/dgp|data gathering panel/i.test(String(eq?.cat ?? ''))
    ),
  ];


  const supplyType = (type = '', capitalize = false) => {
    if (capitalize) {
      const t = String(type).toLowerCase();
      // if (t.includes('supply and install')) return 'Supply and install';
      // if (t.includes('supply')) return 'Supply';
      // return type;
    if (t.includes('supply and install'))
    {
      return ' Supply and install';
    } else {
      return ' Supply';
    }      
    }

    const t = String(type).toLowerCase();
    if (t.includes('supply and install'))
    {
       return ' supply and install';
    } else {
      return ' supply';
    }
    // if (t.includes('supply')) return ' supply';
    // return type;
  }

  // Equipment display
  const renderEquipment = (startNum = 1, supplyType) => (
    <ol className="mb-2 pl-4 list-decimal" start={startNum}>
      {equipmentSorted.map((eq, idx) => {
        const qtyNum = Number(eq?.quantity ?? 0) || 0;
        return (
          <li key={idx}>
            {supplyType} <b>{numberToWords(qtyNum).toLowerCase()}</b> ({qtyNum}) {eq?.device}
            <span className="text-gray-500"> {eq?.description ? ` - ${eq.description}` : ''}</span>
          </li>
        );
      })}
      <li>Supply all Programming for correct operation.</li>
      <li>Generate Shop Drawings and Submittals.</li>
      <li>Provide NICET Level IV Project Management.</li>
      <li>Test/Demo with Building Personnel.</li>
    </ol>
  );

  // Notes (qualifications / terms)
  const renderNotesList = (arr) => (
    <ol className="pl-4 list-decimal">
      {(arr || []).map((row, idx) =>
        row?.selected_id !== false ? (
          row?.notes === 'Price reflects engineer drawings dated:' ? (
            <li key={idx}>
              {row.notes} <DateFormat date={dwgDateVal} />.
            </li>
          ) : (
            <li key={idx}>{row?.notes}</li>
          )
        ) : null
      )}
    </ol>
  );

  const handleDeleteConfirmationDate = async (dwgDateId) => {
    try {
      const response = await axios.post(`${apiUrl}/best_crud.php`, {
        status: 'deleteDwgDate',
        note_id: dwgDateId,
      });
      if (response.data?.success) {
        toast.success('Date deleted!', { position: 'top-center' });
        await fetchProposal();
      } else {
        toast.error(response.data?.error || 'Failed to delete date.', { position: 'top-center' });
      }
    } catch (error) {
      toast.error('Error while deleting date.', { position: 'top-center' });
    }
  };

  const handleDwgDateSubmit = async () => {
    if (!dwgDate) return;
    setLoading(true);
    const bodyData = {
      status: 'addDwgDate',
      estimate_id: proposalData?.estimate?.id,
      dwg_date: dwgDate,
    };

    try {
      const response = await axios.post(`${apiUrl}/best_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data?.[0] === 'success' || response.data?.success) {
        toast.success('Drawing confirmation date saved!', { position: 'top-center' });
        await fetchProposal();
      } else {
        toast.error('Failed to save drawing confirmation date', { position: 'top-center' });
      }
    } catch (err) {
      console.error('Error saving drawing confirmation date:', err);
      toast.error('Failed to save drawing confirmation date', { position: 'top-center' });
    } finally {
      setLoading(false);
      setDwgDate('');
    }
  };

  // Download (server-side PDF via mPDF)
  const handleDownloadServerPdf = async () => {
    setDownloading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/gen_pdf.php?estimate=${id}&builder_id=${key}`;
      const response = await axios.get(url, { responseType: 'blob', withCredentials: true });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = `Proposal_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(pdfUrl);
    } catch (err) {
      console.error('Download PDF error:', err);
      toast.error('Unable to download PDF', { position: 'top-center' });
    } finally {
      setDownloading(false);
    }
  };

  // Inline preview (opens a new tab)
  const handlePreviewPdf = () => {
    const url = `${process.env.REACT_APP_API_URL}/gen_pdf.php?estimate=${id}&builder_id=${key}&inline=1`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mx-auto max-w-[1200px] sm:px-6 lg:px-6 py-8 bg-white">
      {/* Header & Address Block */}
      <EstimateBar id={id} name={'proposal'} builder_id={key} />

      <div
        style={{ fontFamily: "Cambria, Cochin, Georgia, Times, 'Times New Roman', serif" }}
        className="mx-auto max-w-[900px] bg-white p-8 rounded shadow"
      >
        <HeadingCp label="Proposal" />

        {dwgDateId && (
          <button
            onClick={() => handleDeleteConfirmationDate(dwgDateId)}
            className="text-red-600 hover:text-red-800 inline-flex gap-1 mb-2"
          >
            <TrashIcon className="h-5 w-5" /> Delete Drawing Confirmation Date
          </button>
        )}

        <br />
        <br />

        <div className="no-print">
          {/* <button
            onClick={handleDownloadServerPdf}
            disabled={downloading || loading}
            className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading ? 'Preparing…' : 'Download PDF'}
          </button> */}
{/* 
          <button
            onClick={handlePreviewPdf}
            disabled={loading}
            className="ml-2 bg-gray-100 text-gray-800 px-4 py-2 rounded shadow hover:bg-gray-200 disabled:opacity-50"
          >
            Preview PDF
          </button> */}
        </div>

        <br />

        <div className="mb-2 text-sm">
          <div><DateFormat date={projectDate} /></div>
          <br />
          <div className="text-base">
            {renderAddressBlock()}
          </div>
        </div>

        {/* Subject Line */}
        <div className="my-8 font-semibold text-lg text-center underline">
          Re: Proposal for Fire Alarm Equipment Supply at: {jobLocation}
        </div>

        {/* Greeting */}
        <div className="mb-4 text-base font-extrabold">Dear {contact.att},</div>

        {/* Intro */}
        <div className="mb-6">
          We are delighted to present our proposal for your consideration and approval regarding the 
          {proposalData.system_type && supplyType(proposalData.system_type)} of your Fire
          Alarm System at <span className="font-bold underline"> {jobLocation}</span>. At Fire Gear., Inc., we are deeply committed to delivering
          exceptional fire alarm solutions, ensuring the utmost safety and security of your premises.
        </div>

        {/* Equipment Section */}
        <div className="mb-3">
          <div className="mb-4">
            Fire Gear., Inc. shall provide the following cutting-edge equipment and outstanding
            services for the successful completion of the project:
          </div>
          <div className="ml-10">{renderEquipment(1, supplyType(proposalData.system_type, true))}</div>
        </div>

        {/* Price Section */}
        <div className="my-10">
          <div className="text-lg font-semibold underline">PRICE:</div>
          <br />
          <span>
            The price to furnish the above-mentioned equipment supply and delivery services amounts to
            <br />
            <span className="font-extrabold">
              {finalPrice ? <CurrencyFormat value={finalPrice} /> : ''}
            </span>
            <span className="ml-2 font-extrabold">
              {finalPrice ? '(' + numberToWordsUSD(finalPrice) + ').' : ''}
            </span>
          </span>
         
            {alternates.length > 0 && (
              <div className="py-2 border-t mt-4">
                <div className="font-semibold mb-3">Alternative price(s):</div>

                {alternates.map((alt, index) => {
                  const altPrice = alt?.alternate_price ?? alt?.alternative_price ?? 0;
                  const altDescription = alt?.description || `Alternate Option ${index + 1}`;

                  return (
                    <div key={alt.id || index} className="mb-4">
                      <div className="font-medium">{altDescription}</div>
                      <span className="font-extrabold">
                        {altPrice ? <CurrencyFormat value={altPrice} /> : ''}
                      </span>
                      <span className="ml-2 font-extrabold">
                        {altPrice ? '(' + numberToWordsUSD(altPrice) + ').' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          <span className="text-sm italic">(Kindly note that the quoted price excludes any applicable sales tax.)</span>
        </div>

        {/* Qualifications */}
        <div className="my-4 mb-6">
          <div className="font-semibold underline text-lg mb-2">QUALIFICATIONS:</div>
          <div className="ml-10">{renderNotesList(allQualifications)}</div>
        </div>

        {/* Terms & Conditions */}
        <div className="my-10 mb-6">
          <div className="font-semibold underline text-lg mb-2">TERMS & CONDITIONS:</div>
          <div className="ml-10">{renderNotesList(allTerms)}</div>
        </div>

        {/* Closing Paragraph */}
        <div className="mt-10">
          Thank you for considering Fire Gear., Inc., as your trusted partner for your fire alarm
          equipment requirements. We genuinely appreciate the opportunity to present our proposal and remain at your
          disposal for any further inquiries or clarifications.
          <br />
          <br />
          <br />
          Sincerely,
          <br />
          {/* <b>{userName}</b> */}
        </div>
      </div>

      {/* Modal for Drawing Confirmation Date */}
      {showDwgDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded p-6 shadow-lg w-full max-w-sm">
            <h2 className="text-lg font-bold mb-4">Add Drawing Confirmation Date</h2>
            <input
              type="date"
              value={dwgDate}
              onChange={(e) => setDwgDate(e.target.value)}
              className="border rounded px-3 py-2 mb-4 w-full"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowDwgDateModal(false)} className="bg-gray-300 px-4 py-2 rounded">
                Close
              </button>
              <button onClick={handleDwgDateSubmit} className="bg-blue-600 text-white px-4 py-2 rounded">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {/* --- end modal --- */}
    </div>
  );
};

export default EstimateProposal;
